import type {
  MasterSearchEntityType,
  MasterSearchItem
} from "api/masterSearch";

interface MasterSearchProductFlags {
  isBoomCRM: boolean;
  isBoomScreen: boolean;
  isRentReporting: boolean;
  isLockboxes: boolean;
  isCrmAnalytics: boolean;
}

const PRODUCT_GATES: Record<
  MasterSearchEntityType,
  (flags: MasterSearchProductFlags) => boolean
> = {
  lead: (flags) => flags.isBoomCRM,
  contact: (flags) => flags.isBoomCRM,
  screen_applicant: (flags) => flags.isBoomScreen,
  report_renter: (flags) => flags.isRentReporting,
  property: (flags) => flags.isBoomCRM,
  listing: (flags) => flags.isBoomCRM,
  lockbox: (flags) => flags.isBoomCRM && flags.isLockboxes
};

const toNumericId = (
  value: string | number | boolean | null | undefined
): number | null => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const listingRoute = (item: MasterSearchItem): string | null => {
  const propertyId = toNumericId(item.context?.property_id);
  const unitId = toNumericId(item.context?.unit_id);
  if (propertyId && unitId) return `/properties/${propertyId}/units/${unitId}`;
  if (propertyId) return `/properties/${propertyId}`;
  return null;
};

const routeFor = (item: MasterSearchItem): string | null => {
  switch (item.type) {
    case "lead":
      return `/leads/${item.id}`;
    case "contact":
      return `/contacts/${item.id}`;
    case "screen_applicant": {
      const applicationId = toNumericId(item.context?.application_id);
      return applicationId ? `/applications/${applicationId}` : null;
    }
    case "report_renter":
      return `/rent-reporting/${item.id}`;
    case "property":
      return `/properties/${item.id}`;
    case "listing":
      return listingRoute(item);
    case "lockbox":
      return `/lockboxes?lockboxId=${item.id}`;
    default:
      return null;
  }
};

// Results must never deep-link into a product route the partner lacks — the
// route table filters those out, so the link would fall through to Not Found.
export const buildDetailRoute = (
  item: MasterSearchItem,
  flags: MasterSearchProductFlags
): string | null => (PRODUCT_GATES[item.type]?.(flags) ? routeFor(item) : null);

/**
 * Types this partner can actually open. The server gates groups per product,
 * but a couple of client routes need a second flag (lockboxes), so ungated
 * groups are dropped before render rather than rendered as dead rows.
 */
export const routableTypes = (
  flags: MasterSearchProductFlags
): MasterSearchEntityType[] =>
  (Object.keys(PRODUCT_GATES) as MasterSearchEntityType[]).filter((type) =>
    PRODUCT_GATES[type](flags)
  );

export interface MasterSearchQuickLink {
  key: "analytics" | "leads" | "applicants";
  label: string;
  route: string;
}

// /leads and /applications read their filters from the URL as JSON-encoded
// {id, name} arrays (see shared/utils/search-params-serialization.ts); the
// name is only the filter chip's display text, ids drive the query.
const idNameParam = (id: number, name: string | null): string =>
  encodeURIComponent(JSON.stringify([{ id: String(id), name: name ?? "" }]));

const contextName = (
  value: string | number | boolean | null | undefined
): string | null => (typeof value === "string" && value !== "" ? value : null);

/**
 * Property/unit-scoped quick links: a property or unit search
 * result also links straight to that property's Analytics dashboard and to
 * the Leads/Applicants lists pre-filtered to it, so a PM typing an address
 * doesn't have to open the property page first. Unit rows filter Leads and
 * Applicants down to the unit.
 */
export const buildQuickLinks = (
  item: MasterSearchItem,
  flags: MasterSearchProductFlags
): MasterSearchQuickLink[] => {
  if (item.type !== "property" && item.type !== "listing") return [];

  const propertyId =
    item.type === "property" ? item.id : toNumericId(item.context?.property_id);
  if (!propertyId) return [];

  // Name fallbacks: the filter chip on the target list renders this string,
  // so a missing name must not produce an empty pill.
  const propertyName =
    item.type === "property"
      ? item.title
      : (contextName(item.context?.property_name) ?? `Property #${propertyId}`);
  const unitId =
    item.type === "listing" ? toNumericId(item.context?.unit_id) : null;
  const unitName = unitId
    ? (contextName(item.context?.unit_name) ?? item.title)
    : null;

  const links: MasterSearchQuickLink[] = [];

  // The BoomCRM analytics tab is its own feature, not implied by isBoomCRM;
  // without it the tab doesn't render and the deep link would fall back to
  // whatever tab the partner has first. Property-scoped also on unit rows:
  // in the units filter a whole-property selection already covers its units,
  // and the quick link targets the property's dashboard from either row.
  if (flags.isCrmAnalytics)
    links.push({
      key: "analytics",
      label: "Analytics",
      route: `/analytics?tab=crm&property_ids=${propertyId}`
    });

  if (flags.isBoomCRM) {
    const unitParam = unitId ? `&units=${idNameParam(unitId, unitName)}` : "";
    links.push({
      key: "leads",
      label: "Leads",
      route:
        `/leads?properties=${idNameParam(propertyId, propertyName)}` + unitParam
    });
  }

  if (flags.isBoomScreen) {
    const unitParam = unitId ? `&units=${idNameParam(unitId, unitName)}` : "";
    links.push({
      key: "applicants",
      label: "Applicants",
      route:
        `/applications?properties=${idNameParam(propertyId, propertyName)}` +
        unitParam
    });
  }

  return links;
};
